import { CHECK_VARIANTS, DATE_FORMAT, PDF_MIME_TYPE } from 'constants/candidate-portal';
import { saveAs } from 'file-saver';
import moment from 'moment';

/**
 * Formats check status for display
 * @param {string} status - Check status
 * @returns {string} Formatted status string
 */
export const formatCheckStatus = status => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'in-progress':
      return 'In Progress';
    case 'submitted':
      return 'Submitted';
    case 'candidate':
      return 'Pending';
    case 'overdue':
      return 'Overdue';
    case 'canceled':
      return 'Canceled';
    default:
      return status;
  }
};

/**
 * Generates a filename for report downloads
 * @param {Object} options - Options object
 * @param {string} [options.checkName] - Name of the check
 * @param {string|number} [options.checkId] - Check ID
 * @param {string|number} [options.subCheckId] - Sub-check ID
 * @param {string} [options.prefix='report'] - Filename prefix
 * @param {string} [options.extension='pdf'] - File extension
 * @returns {string} Generated filename
 */
export const generateReportFileName = ({
  checkName,
  checkId,
  subCheckId,
  prefix = 'report',
  extension = 'pdf',
} = {}) => {
  if (checkName) {
    return `${checkName}_${prefix}.${extension}`;
  }

  const reportId = checkId || subCheckId || 'download';
  return `${prefix}_${reportId}.${extension}`;
};

/**
 * Formats a date for display with validation
 * @param {string|Date} date - Date to format
 * @param {string} format - Date format (default: DD/MM/YYYY)
 * @returns {string} Formatted date or 'Invalid date' if invalid
 */
export const formatDateForDisplay = (date, format = DATE_FORMAT) => {
  if (!date) {
    return '';
  }

  const momentDate = moment(date);
  if (!momentDate.isValid()) {
    return 'Invalid date';
  }

  return momentDate.format(format);
};

/**
 * Converts a Blob to a data URL (avoids blob: URLs for CSP).
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
const blobToDataUrl = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Processes file response and converts it to a format suitable for preview.
 * Uses data URLs or HTTPS URLs only (no blob: URLs) so CSP does not need blob:.
 *
 * @param {string|Object|Blob} result - File URL, base64 string, data URL, object with url property, or Blob
 * @param {string|number} fileId - File identifier
 * @returns {Promise<Object>} File object with url, file_type, id, and metadata
 * @throws {Error} If file data is invalid
 */
export const processFileResponse = async (result, fileId) => {
  if (!result) {
    throw new Error('No file data provided');
  }

  let fileUrl;

  if (result instanceof Blob) {
    fileUrl = await blobToDataUrl(result);
  } else if (typeof result === 'object' && result !== null && result.url) {
    fileUrl = result.url;
  } else if (typeof result === 'string') {
    const isUrl =
      result.startsWith('http://') || result.startsWith('https://') || result.startsWith('data:');

    if (isUrl) {
      fileUrl = result;
    } else {
      // Assume base64; use data URL directly so we don't need blob: in CSP
      try {
        fileUrl = `data:${PDF_MIME_TYPE};base64,${result}`;
      } catch (e) {
        console.error('Error building data URL from base64:', e);
        throw new Error('Invalid file data: Unable to convert base64 to data URL');
      }
    }
  } else {
    throw new Error(
      `Invalid file data: Expected string, object with url property, or Blob, but got ${typeof result}`,
    );
  }

  return {
    url: fileUrl,
    file_type: PDF_MIME_TYPE,
    id: fileId,
    metadata: {},
  };
};

/**
 * Finds a check by ID in an array of checks
 * @param {Array} checks - Array of check objects
 * @param {string|number} checkId - Check ID to find
 * @returns {Object|null} Found check or null
 */
export const findCheckById = (checks, checkId) => {
  if (!checks || !Array.isArray(checks) || !checkId) {
    return null;
  }
  return checks.find(check => check?.id === checkId) || null;
};

/**
 * Finds the appropriate variant configuration for a check based on its state
 * @param {Object} check - Check object
 * @param {Object} icons - Object containing icon components (disputed, releasedToClient, actionRequired, ready)
 * @returns {Object} Variant configuration object with variant, title, message, and icon
 */
export const findCheckVariant = (check, icons) => {
  if (check?.disputed) {
    return {
      ...CHECK_VARIANTS.disputed,
      icon: icons.disputed,
    };
  } else if (check?.releasedToClient) {
    return {
      ...CHECK_VARIANTS.releasedToClient,
      icon: icons.releasedToClient,
    };
  } else if (check?.actionRequired) {
    return {
      ...CHECK_VARIANTS.actionRequired,
      icon: icons.actionRequired,
    };
  }

  // Default to actionRequired
  return {
    ...CHECK_VARIANTS.ready,
    icon: icons.ready,
  };
};

/**
 * Downloads a file from a URL
 * @param {string} url - File URL to download
 * @param {string} fileName - Name for the downloaded file
 * @returns {Promise<void>}
 * @throws {Error} If download fails
 */
export const downloadFile = async (url, fileName = 'download.pdf') => {
  if (!url) {
    throw new Error('File URL is required');
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  if (!blob || blob.size === 0) {
    throw new Error('Downloaded file is empty');
  }

  saveAs(blob, fileName);
};

/**
 * Gets display name for consent file based on filename
 * @param {string} fileName - File name to check
 * @returns {string} Display name for the consent file
 */
export const getConsentDisplayName = fileName => {
  if (!fileName || typeof fileName !== 'string') {
    return 'Application Consents';
  }

  const lowerFileName = fileName.toLowerCase();
  if (lowerFileName.includes('fcra')) {
    return 'FCRA Disclosures & Authorizations';
  }

  return 'Application Consents';
};
